import { AGEING_BUCKETS, type AgeingTone } from '@bright/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme.
 *
 * The inline script in index.html has already put the right `data-theme` on <html>
 * before the first paint; this provider takes over from there, keeps the choice in
 * localStorage, and follows the operating system while the mode is "system".
 *
 * It also owns `readChartColor`, because Recharts wants concrete colour strings and
 * every colour in this product is a CSS variable. Reading them through here means a
 * chart re-reads its palette on a theme change instead of staying navy on a light page.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'bsa.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeContextValue {
  /** What the user chose. */
  mode: ThemeMode;
  /** What is actually on screen. */
  theme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** light → dark → system → light. */
  cycleMode: () => void;
  /** Straight flip between light and dark, leaving "system" behind. */
  toggle: () => void;
  /**
   * Resolves a CSS custom property to a colour string. Pass either
   * `--color-ch-dealer` (a Tailwind theme name) or `--c-ch-dealer` (a raw token).
   * The identity of this function changes with the theme, so memoised chart configs
   * that depend on it recompute at exactly the right moment.
   */
  readChartColor: (varName: string, fallback?: string) => string;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Storage can be unavailable (private browsing, blocked cookies). Not fatal.
  }
  return 'system';
}

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

function resolve(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? systemTheme() : mode;
}

/**
 * Reads a live CSS variable off the document root.
 *
 * Exported standalone as well as through the hook, so non-React helpers (a chart
 * tooltip formatter, a canvas export) can reach the palette. Returns the fallback
 * when the variable is missing rather than an empty string, which would make
 * Recharts draw nothing at all.
 */
export function readChartColor(varName: string, fallback = 'currentColor'): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') return fallback;
  const name = varName.startsWith('--') ? varName : `--${varName}`;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolve(readStoredMode()));

  // Apply. `colorScheme` matters as much as the attribute: it is what makes native
  // scrollbars, date pickers and form controls switch with the rest of the page.
  useEffect(() => {
    const resolved = resolve(mode);
    setTheme(resolved);
    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    root.style.colorScheme = resolved;
    // Keep the browser chrome on a phone in step with the page.
    const meta = document.querySelector('meta[name="theme-color"]');
    const paper = readChartColor('--c-paper', '');
    if (meta && paper) meta.setAttribute('content', paper);
  }, [mode]);

  // Follow the operating system, but only while the user has not made a choice.
  useEffect(() => {
    if (mode !== 'system' || !window.matchMedia) return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => {
      const resolved = systemTheme();
      setTheme(resolved);
      document.documentElement.setAttribute('data-theme', resolved);
      document.documentElement.style.colorScheme = resolved;
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preference simply will not persist. The session still works.
    }
  }, []);

  const cycleMode = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light');
  }, [mode, setMode]);

  const toggle = useCallback(() => {
    setMode(resolve(mode) === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      theme,
      setMode,
      cycleMode,
      toggle,
      // Depends on `theme` on purpose: charts memoising on this function pick up the
      // new palette the moment the attribute flips.
      readChartColor: (varName: string, fallback?: string) => readChartColor(varName, fallback),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, theme, setMode, cycleMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside a ThemeProvider');
  return ctx;
}

/**
 * The channel palette, in custody order, resolved for the current theme.
 * Charts that colour by channel should use this rather than picking their own.
 */
export function useChannelPalette(): Record<
  'bright' | 'outlet' | 'dealer' | 'subdealer' | 'customer' | 'pending',
  string
> {
  const { theme } = useTheme();
  return useMemo(
    () => ({
      bright: readChartColor('--c-ch-bright'),
      outlet: readChartColor('--c-ch-outlet'),
      dealer: readChartColor('--c-ch-dealer'),
      subdealer: readChartColor('--c-ch-subdealer'),
      customer: readChartColor('--c-ch-customer'),
      pending: readChartColor('--c-ch-pending'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  );
}

/** The five-step ageing ramp, resolved for the current theme. */
export function useAgeingPalette(): Record<AgeingTone, string> {
  const { theme } = useTheme();
  return useMemo(
    () =>
      // Keyed off the buckets themselves, so a new band cannot be added to the
      // domain without its colour arriving with it.
      AGEING_BUCKETS.reduce(
        (palette, bucket) => {
          palette[bucket.tone] = readChartColor(`--c-age-${bucket.tone}`);
          return palette;
        },
        {} as Record<AgeingTone, string>,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  );
}
